// src/components/Pages/RHPanel.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import './../../style/Vars.css';
import './../../style/MobileStyles.css';

// 🔹 Endpoints y modelos de Seguridad
import { ENDPOINTS } from "../Seguridad/Utils/Endpoints";
import { Models } from "../Seguridad/Utils/ModelsSeguridad";

// 🔹 Usuario en sesión
import { getSessionUserId } from "../../utils/SessionUser";

// 🔹 Componentes inline (sin navegación)
import Organigrama from "../RH/Components/Organigrama/Organigrama";

// Íconos para cada submódulo
const submoduleIcons = {
  empleados: '👥',
  puestos: '💼',
  areasNegocio: '🏢',
  unidadesOrg: '📂',
  turnos: '⏰',
  diasInhabiles: '📅',
  organigrama: '🗂️',
};

// Función auxiliar para asignar colores
const getModuleColor = (id) => {
  const colors = {
    empleados: 'rose',
    puestos: 'amber',
    areasNegocio: 'blue',
    unidadesOrg: 'indigo',
    turnos: 'orange',
    diasInhabiles: 'green',
    organigrama: 'cyan',
  };
  return colors[id] || 'cyan';
};

// ===================================================================
// Definición base de los submódulos de RH
// ===================================================================
const RH_SUBMODULES = [
  {
    id: "empleados",
    title: "Catálogo de Empleados",
    description: "Gestión de personal, datos generales y asignaciones.",
    path: "/menu/dynamic?path=RH/Components/Catalogos/Empleados/EmpleadosCatalog.jsx",
  },
  {
    id: "turnos",
    title: "Gestión de Turnos",
    description: "Configuración de horarios laborales y turnos.",
    path: "/menu/dynamic?path=RH/Components/Catalogos/Turno/TurnosCatalog.jsx",
  },
  {
    id: "diasInhabiles",
    title: "Días Inhábiles",
    description: "Calendario de días festivos y no laborables.",
    path: "/menu/dynamic?path=RH/Components/Catalogos/DiasFestivos/DiasFestivosInhabilesCatalog.jsx",
  },
  {
    id: "puestos",
    title: "Catálogo de Puestos",
    description: "Definición de perfiles y cargos de la organización.\n",
    path: "/menu/dynamic?path=General/Components/Catalogos/Puesto/PuestoCatalog.jsx",
  },
  {
    id: "areasNegocio",
    title: "Áreas de Negocio",
    description: "Estructura de áreas y departamentos.",
    path: "/menu/dynamic?path=General/Components/Catalogos/AreaNegocio/AreaNegocioCatalog.jsx",
  },
  {
    id: "unidadesOrg",
    title: "Unidades Organizacionales",
    description: "Gestión de centros de costos y unidades de la empresa.",
    path: "/menu/dynamic?path=General/Components/Catalogos/UnidadOrganizacional/UnidadOrganizacionalCatalog.jsx",
  },
  {
    id: "organigrama",
    title: "Organigrama",
    description: "Visualizador de estructura organizacional y empleados.",
    path: "/menu/rh/organigrama",
  },
];

const endpointRecursos = ENDPOINTS.recursos;
const modeloRecurso = Models.catRecursos;

function RHPanel() {
  const navigate = useNavigate();
  const [sessionUserId] = useState(() => getSessionUserId());
  const [visibleSubmodules, setVisibleSubmodules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  // null = mostrar grid, string = id del módulo inline activo
  const [activeInline, setActiveInline] = useState(null);

  const handleClick = (submodule) => {
    if (submodule.inline) {
      setActiveInline(submodule.id);
    } else {
      navigate(submodule.path);
    }
  };

  useEffect(() => {
    async function loadRecursos() {
      try {
        if (!sessionUserId) {
          setErrorMsg("No se pudo identificar al usuario en sesión.");
          setVisibleSubmodules([]);
          setLoading(false);
          return;
        }

        const url = `${endpointRecursos}/${sessionUserId}/Panel`;
        const res = await axios.get(url);
        const recursos = (res.data || []).map(modeloRecurso);

        const allowedIds = new Set(
          recursos
            .filter(r => (r.tipo || "").toLowerCase() === "panel" && (r.ruta || "").trim() !== "")
            .map(r => (r.ruta || "").trim())
        );

        let filtered = RH_SUBMODULES.filter(m => allowedIds.has(m.id));

        setVisibleSubmodules(filtered);
        setLoading(false);

        if (!filtered.length) {
          setErrorMsg("No tienes acceso a ningún módulo de Recursos Humanos.");
        }
      } catch (err) {
        console.error("[RHPanel] Error:", err);
        setErrorMsg("Ocurrió un error al cargar los permisos de RH.");
        setVisibleSubmodules([]);
        setLoading(false);
      }
    }

    loadRecursos();
  }, [sessionUserId]);

  // Vista inline activa: reemplaza el grid con el componente + breadcrumb
  if (activeInline) {
    const activeModule = RH_SUBMODULES.find(m => m.id === activeInline);
    const InlineComponent = activeModule?.component;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Breadcrumb / botón de regreso */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          padding: '0.6rem 1.25rem',
          background: '#ffffff',
          borderBottom: '1px solid #e2e8f0',
          flexShrink: 0,
        }}>
          <button
            onClick={() => setActiveInline(null)}
            style={{
              background: 'none',
              border: '1px solid #cbd5e1',
              borderRadius: '6px',
              padding: '0.3rem 0.75rem',
              cursor: 'pointer',
              fontSize: '0.875rem',
              color: '#475569',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
            }}
          >
            ← Volver a RH
          </button>
          <span style={{ color: '#94a3b8', fontSize: '0.875rem' }}>
            {submoduleIcons[activeInline]} {activeModule?.title}
          </span>
        </div>

        {/* Componente inline que ocupa el resto del espacio */}
        <div style={{ flex: 1, overflow: 'hidden' }}>
          {InlineComponent && <InlineComponent />}
        </div>
      </div>
    );
  }

  // Vista por defecto: grid de tarjetas
  return (
    <div className="modules-container">
      <h1>Recursos Humanos</h1>

      {loading ? (
        <div className="modules-loading">Cargando opciones...</div>
      ) : errorMsg ? (
        <div className="modules-error">{errorMsg}</div>
      ) : (
        <div className="modules-grid">
          {visibleSubmodules.map((submodule) => (
            <div
              key={submodule.id}
              className={`module-card module-card--${getModuleColor(submodule.id)}`}
              onClick={() => handleClick(submodule)}
              role="button"
              tabIndex={0}
              onKeyPress={(e) => e.key === 'Enter' && handleClick(submodule)}
            >
              <div className="module-card__title">{submodule.title}</div>
              <div className="module-card__description">{submodule.description}</div>
              <div className="module-card__icon" aria-hidden="true">
                {submoduleIcons[submodule.id] || '⚙️'}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default RHPanel;
